import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useCollaborativeForm } from "../../../hooks/useCollaborativeForm";
import {
  ChecklistService,
  type ChecklistInstanceAnswer as ApiChecklistInstanceAnswer,
} from "../../../services/checklistService";
import {
  CollaborationIndicator,
  UpdateAnimationOverlay,
} from "../../CollaborationIndicator";
import { icons } from "../../icons.tsx";
import { Alert } from "../../ui/Alert.tsx";
import { TemporaryQuestionIdToCategoryMapping } from "./temporary-mappings.ts";

// Types
interface AuditorQuestion {
  for_auditor: boolean;
  id: number;
  lab_type: "wet" | "dry";
  level: string;
  question: string;
  suggestion: string;
}

export interface ChecklistQuestion {
  id: string;
  question: string;
  suggestion: string | null;
}

interface ChecklistProps {
  questions: ChecklistQuestion[];
  onSubmit: (comments?: string) => void;
  certificationId: number;
  instanceId: number;
  labId: string;
  answers: ApiChecklistInstanceAnswer[];
  checklistId: string;
  previewMode?: boolean;
  labType?: "wet" | "dry";
  tier?: "bronze" | "silver" | "gold";
  auditorMode?: boolean;
  isSubmitted?: boolean;
  isAuditorMode: boolean;
  setIsAuditorMode: (value: boolean) => void;
}

type ChecklistResponseValue = "yes" | "no" | "na";

const mapAnswerToResponse = (
  answer: ApiChecklistInstanceAnswer["answer"],
): ChecklistResponseValue | undefined => {
  switch (answer) {
    case "y":
      return "yes";
    case "n":
      return "no";
    case "x":
      return "na";
    default:
      return undefined;
  }
};

const SaveStatusIndicator = ({
  status,
  className = "",
}: {
  status: "idle" | "saving" | "saved" | "error";
  className?: string;
}) => {
  const statusClass =
    status === "saving"
      ? "text-primary"
      : status === "saved"
        ? "text-success"
        : "text-error";

  return (
    <div className={`min-h-6 flex items-center justify-end ${className}`}>
      {status !== "idle" && (
        <div
          className={`flex items-center gap-2 text-sm font-medium ${statusClass}`}
        >
          {status === "saving" && (
            <>
              <span className="loading loading-spinner loading-xs"></span>
              <span>Saving...</span>
            </>
          )}
          {status === "saved" && (
            <>
              <span aria-hidden="true">✓</span>
              <span>Saved</span>
            </>
          )}
          {status === "error" && (
            <>
              <span aria-hidden="true">✕</span>
              <span>Error saving</span>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Updated mock data with correct question counts
const dryLabAuditorQuestions: AuditorQuestion[] = [
  {
    for_auditor: true,
    id: 52,
    lab_type: "dry",
    level: "bronze",
    question: "Do team meeting discussions include sustainability topics?",
    suggestion: "Recommend adding it to meeting agendas or presentations.",
  },
  {
    for_auditor: true,
    id: 53,
    lab_type: "dry",
    level: "bronze",
    question: "Has the team leader or PI promoted sustainability to the group?",
    suggestion:
      "Suggest they send a short internal message to endorse the program.",
  },
  {
    for_auditor: true,
    id: 51,
    lab_type: "dry",
    level: "bronze",
    question: "Is the lab aware who their Lab ZER0 Champion is?",
    suggestion:
      "Recommend nominating one and sharing their name with the team.",
  },
  {
    for_auditor: true,
    id: 132,
    lab_type: "dry",
    level: "bronze",
    question:
      "Has the lab created a sustainability document or induction for new members?",
    suggestion:
      "Request for a document or email to be created for new future lab members.",
  },
  {
    for_auditor: true,
    id: 54,
    lab_type: "dry",
    level: "bronze",
    question:
      "Has the team considered reducing food and drink single-use items?",
    suggestion: "Suggest discussing and bulk-buying reusable items as a team.",
  },
  {
    for_auditor: true,
    id: 55,
    lab_type: "dry",
    level: "bronze",
    question: "Are e-waste education materials clearly displayed?",
    suggestion:
      "Recommend printing and displaying relevant posters near bin stations.",
  },
  {
    for_auditor: true,
    id: 56,
    lab_type: "dry",
    level: "bronze",
    question:
      "Is there a clearly located e-waste bin and is everyone aware of it?",
    suggestion:
      "Suggest signage and a quick walk-through to show its location.",
  },
  {
    for_auditor: true,
    id: 57,
    lab_type: "dry",
    level: "bronze",
    question:
      "Has the organisations data deletion policy been shared with the lab?",
    suggestion: "Recommend sharing policy link and setting a recurring review.",
  },
  {
    for_auditor: true,
    id: 58,
    lab_type: "dry",
    level: "bronze",
    question: "Has a long-term data retention plan been developed?",
    suggestion: "Recommend a group discussion and documenting the plan.",
  },
  {
    for_auditor: true,
    id: 59,
    lab_type: "dry",
    level: "bronze",
    question: "Ask for evidence of plan agreed to by all ",
    suggestion: "Ask for evidence of plan agreed to by all.",
  },
  {
    for_auditor: true,
    id: 60,
    lab_type: "dry",
    level: "bronze",
    question: "Has the group discussed ways to reduce paper use?",
    suggestion:
      "Suggest starting with default digital settings and reducing printing.",
  },
  {
    for_auditor: true,
    id: 61,
    lab_type: "dry",
    level: "bronze",
    question: "Is there a standardised file/folder naming convention in place?",
    suggestion: "Recommend drafting and sharing a standard template.",
  },
  {
    for_auditor: true,
    id: 62,
    lab_type: "dry",
    level: "bronze",
    question: "Has the team been informed on disabling energy saving screens?",
    suggestion:
      "Recommend sharing a digital tip sheet or including it in onboarding.",
  },
  {
    for_auditor: true,
    id: 63,
    lab_type: "dry",
    level: "bronze",
    question: "Has equipment use (e.g. dual monitors) been reviewed?",
    suggestion: "Suggest a quick internal review of actual needs.",
  },
  {
    for_auditor: true,
    id: 64,
    lab_type: "dry",
    level: "bronze",
    question: "Are staff aware of screen brightness and energy use?",
    suggestion: "Recommend reminders via meetings or posters.",
  },
  {
    for_auditor: true,
    id: 65,
    lab_type: "dry",
    level: "bronze",
    question: "Are energy-saving settings enabled on lab computers?",
    suggestion: "Recommend enabling them by default and sharing how-to guides.",
  },
  {
    for_auditor: true,
    id: 66,
    lab_type: "dry",
    level: "bronze",
    question:
      "Is passive power use (sleep mode, auto-off) reduced on lab devices?",
    suggestion: "Recommend enabling these features on all applicable devices.",
  },
];

// Mock data for wet lab auditor questions
const wetLabAuditorQuestions: AuditorQuestion[] = [
  {
    for_auditor: true,
    id: 19,
    lab_type: "wet",
    level: "bronze",
    question: "Do team meeting discussions include sustainability topics?",
    suggestion: "Recommend adding it to meeting agendas or presentations.",
  },
  {
    for_auditor: true,
    id: 20,
    lab_type: "wet",
    level: "bronze",
    question: "Has the team leader or PI promoted sustainability to the group?",
    suggestion:
      "Suggest they send a short internal message to endorse sustainable practices.",
  },
  {
    for_auditor: true,
    id: 18,
    lab_type: "wet",
    level: "bronze",
    question: "Is the lab aware who their Lab ZER0 Champion is?",
    suggestion:
      "Recommend nominating one and sharing their name with the team.",
  },
  {
    for_auditor: true,
    id: 131,
    lab_type: "wet",
    level: "bronze",
    question:
      "Has the lab created a sustainability document or induction for new members?",
    suggestion:
      "Request for a document or email to be created for new future lab members.",
  },
  {
    for_auditor: true,
    id: 21,
    lab_type: "wet",
    level: "bronze",
    question: "Is clear signage visible for all waste streams?",
    suggestion:
      "Suggest using signage templates and confirming all waste types are covered.",
  },
  {
    for_auditor: true,
    id: 22,
    lab_type: "wet",
    level: "bronze",
    question: "Are bins available for all required waste streams?",
    suggestion:
      "Ask which are missing and suggest contacting Facilities or Lab Management.",
  },
  {
    for_auditor: true,
    id: 23,
    lab_type: "wet",
    level: "bronze",
    question: "Are bins placed in logical, easy-to-access locations?",
    suggestion: "Recommend mapping current placement and adjusting as needed.",
  },
  {
    for_auditor: true,
    id: 24,
    lab_type: "wet",
    level: "bronze",
    question: "Is someone responsible for checking bins and signage regularly?",
    suggestion: "Recommend a simple roster for monthly checks.",
  },
  {
    for_auditor: true,
    id: 25,
    lab_type: "wet",
    level: "bronze",
    question:
      "Are fridges/freezers seals free from visible damage? (i.e. no gaps, cracks, tears)",
    suggestion:
      "Recommend reporting damage to Facilities or Manager to coordinate repair.",
  },
  {
    for_auditor: true,
    id: 26,
    lab_type: "wet",
    level: "bronze",
    question: "Are units free from heavy frost or ice buildup?",
    suggestion: "Recommend defrosting and adding to maintenance routine.",
  },
  {
    for_auditor: true,
    id: 27,
    lab_type: "wet",
    level: "bronze",
    question:
      "Is there a planned defrosting schedule for -20 and -80 freezers?",
    suggestion:
      "Suggest creating one and involving Facilities, Manager sign-off.",
  },
  {
    for_auditor: true,
    id: 28,
    lab_type: "wet",
    level: "bronze",
    question:
      "Are all fridges and freezers being used efficiently? i.e. >50% full?",
    suggestion:
      "Suggest consolidating samples or temporarily turning off underused units.",
  },
  {
    for_auditor: true,
    id: 29,
    lab_type: "wet",
    level: "bronze",
    question: "Is an inventory system used for cold storage?",
    suggestion: "Recommend starting a spreadsheet or using inventory software.",
  },
  {
    for_auditor: true,
    id: 30,
    lab_type: "wet",
    level: "bronze",
    question: "Has the team been informed about energy-saving practices?",
    suggestion: "Recommend sharing a tip sheet or including in onboarding.",
  },
  {
    for_auditor: true,
    id: 31,
    lab_type: "wet",
    level: "bronze",
    question: "Has equipment use (e.g. dual monitors) been reviewed?",
    suggestion: "Suggest a quick internal review of actual needs.",
  },
  {
    for_auditor: true,
    id: 32,
    lab_type: "wet",
    level: "bronze",
    question: "Are staff aware of screen brightness and energy use?",
    suggestion: "Recommend reminders via meetings or posters.",
  },
  {
    for_auditor: true,
    id: 33,
    lab_type: "wet",
    level: "bronze",
    question: "Are energy-saving settings enabled on lab computers?",
    suggestion: "Recommend enabling them by default and sharing how-to guides.",
  },
  {
    for_auditor: true,
    id: 34,
    lab_type: "wet",
    level: "bronze",
    question:
      "Is passive power use (sleep mode, auto-off) reduced on lab devices?",
    suggestion: "Recommend enabling these features on all applicable devices.",
  },
];

// Bronze tier categories
const ORDERED_WET_CATEGORIES_BRONZE = [
  "Green Culture & Team Communication",
  "Lab Waste Management",
  "Cold Storage",
  "Computer Energy Usage",
  "Misc",
];

const ORDERED_DRY_CATEGORIES_BRONZE = [
  "Green Culture & Team Communication",
  "Eco-Office",
  "Computer Energy Usage",
  "Data Storage",
  "Misc",
];

// Silver tier categories — order per issue #326 (Purchasing, Green Chemistry, Transport)
const ORDERED_WET_CATEGORIES_SILVER = [
  "Procurement",
  "Green Chemistry",
  "Sustainable Transport",
  "Misc",
];

const ORDERED_DRY_CATEGORIES_SILVER = [
  "Sustainable Transport",
  "Procurement",
  "Green Chemistry",
  "Misc",
];

// Gold tier categories
const ORDERED_WET_CATEGORIES_GOLD = [
  "Eco Offices",
  "Equipment",
  "Water",
  "Misc",
];

const ORDERED_DRY_CATEGORIES_GOLD = [
  "Eco Offices",
  "Equipment",
  "Water",
  "Misc",
];

// Helper to get ordered categories by tier and lab type
const getOrderedCategories = (
  labType: "wet" | "dry",
  tier: "bronze" | "silver" | "gold",
): string[] => {
  const categoryMap = {
    bronze: {
      wet: ORDERED_WET_CATEGORIES_BRONZE,
      dry: ORDERED_DRY_CATEGORIES_BRONZE,
    },
    silver: {
      wet: ORDERED_WET_CATEGORIES_SILVER,
      dry: ORDERED_DRY_CATEGORIES_SILVER,
    },
    gold: {
      wet: ORDERED_WET_CATEGORIES_GOLD,
      dry: ORDERED_DRY_CATEGORIES_GOLD,
    },
  };
  return categoryMap[tier]?.[labType] || categoryMap.bronze[labType];
};

/**
 * Mapping to show downloadable resources, given a question category
 * This is a temporary measure until we have proper backend support
 */
// Tier-specific resource mappings
const CategoryToResourceMapping: Record<
  string,
  Record<string, { name: string; url: string }[]>
> = {
  bronze: {
    "Eco-Office": [
     {
      name: "E-waste Resource",
      url: "/docs/E-waste Resource.pdf",
    },
    {
      name: "Resin Resource",
      url: "/docs/Resin Resource.pdf",
    },
    ],
    "Data Storage": [
      {
        name: "File Naming Resource",
        url: "/docs/File Naming Resource.pdf",
      },
      {
        name: "Data Storage Resource",
        url: "/docs/Data Storage Resource.pdf",
      },
    ],
    "Computer Energy Usage": [
      {
        name: "Computer Screen Resource",
        url: "/docs/Computer Screen Resource.pdf",  
      },
    ],
    "Lab Waste Management": [
      {
        name: "Triangle Myths",
        url: "/docs/Wet-Lab-Resources-Bronze-Triangle-Myths.pdf",
      },
      {
        name: "Lab Recycling",
        url: "/docs/Wet-Lab-Resources-Bronze-Lab-Recycling.pdf",
      },
    ],
    "Cold Storage": [
      {
        name: "Freezer Use Guide",
        url: "/docs/Wet-Lab-Resources-Bronze-Freezer-Use.pdf",
      },
      {
        name: "Cold Storage Monitoring Roster",
        url: "/docs/Cold Storage Unit – Monthly Maintenance Roster.pdf",
      },
    ],
  },
  silver: {
    "Sustainable Transport": [
      // {
      //   name: "Sustainable Transport Resource",
      //   url: "/docs/Wet-Lab-Resources-Silver-Transport.pdf",
      // },
      {
        name: "Sustainable Transport Resource 2026",
        url: "/docs/Sustainable Transport Resource 2026.pdf",
      },
    ],
    Procurement: [
      // {
      //   name: "Procurement Resource",
      //   url: "/docs/Wet-Lab-Resources-Silver-Procurement.pdf",
      // },
      {
        name: "Emissions Resource",
        url: "/docs/Emissions Resource 2026.pdf",
      },
    ],
    "Green Chemistry": [
      // {
      //   name: "Green Chemistry Resource",
      //   url: "/docs/Wet-Lab-Resources-Silver-Green-Chemistry.pdf",
      // },
      {
        name: "12 Principles of Green Chemistry",
        url: "/docs/12 Principles of Green Chemistry Resource 2026.pdf",
      },
    ],
  },
  gold: {
    "Eco Offices": [
      {
        name: "Eco Offices Resource",
        url: "/docs/Wet-Lab-Resources-Gold-Eco-Offices.pdf",
      },
    ],
    Equipment: [
      {
        name: "Equipment Resource",
        url: "/docs/Wet-Lab-Resources-Gold-Equipment.pdf",
      },
    ],
    Water: [
      {
        name: "Water Conservation Resource",
        url: "/docs/Wet-Lab-Resources-Gold-Water.pdf",
      },
    ],
  },
};

// Helper to get resources for a category by tier
const getResourcesForCategory = (
  tier: "bronze" | "silver" | "gold",
  category: string,
): { name: string; url: string }[] | undefined => {
  return CategoryToResourceMapping[tier]?.[category];
};

// Tier-specific button colors
const tierButtonColors = {
  bronze: {
    bg: "bg-[#f7c495]",
    hover: "hover:bg-[#f7c495cc]",
  },
  silver: {
    bg: "bg-[#C0C0C0]",
    hover: "hover:bg-[#B0B0B0]",
  },
  gold: {
    bg: "bg-[#F5C91B]",
    hover: "hover:bg-[#E5B82A]",
  },
};

// Modal Components
const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
}) => {
  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box rounded-xl">
        <h3 className="font-bold text-lg">{title}</h3>
        <p className="py-4">{message}</p>
        <div className="modal-action">
          <button className="btn btn-ghost" onClick={onClose}>
            {cancelText}
          </button>
          <button className="btn btn-primary" onClick={onConfirm}>
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const ResourceModal = ({
  isOpen,
  onClose,
  resources,
}: {
  isOpen: boolean;
  onClose: () => void;
  resources: { name: string; url: string }[];
}) => {
  if (!isOpen || !resources || resources.length === 0) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-4xl rounded-xl">
        <h3 className="font-bold text-lg mb-4">Available Resources</h3>
        <div className="w-full bg-gray-50 p-4 rounded-lg mb-4">
          <div className="space-y-3">
            {resources.map((resource, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-primary transition-colors"
              >
                <div className="flex items-center gap-3">
                  <img
                    width={20}
                    src={icons.download}
                    alt="pdf"
                    className="opacity-60"
                  />
                  <span className="font-medium text-gray-800">
                    {resource.name}
                  </span>
                </div>
                <a
                  href={resource.url}
                  download
                  className="btn btn-sm btn-primary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    width={12}
                    src={icons.download}
                    alt="download"
                    className="mr-1"
                  />
                  Download
                </a>
              </div>
            ))}
          </div>
        </div>
        <div className="modal-action">
          <button className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export const Checklist = ({
  questions,
  onSubmit: answerSubmit,
  certificationId,
  instanceId,
  answers,
  checklistId,
  previewMode,
  labType = "wet",
  tier = "bronze",
  auditorMode = false,
  isSubmitted = false,
  isAuditorMode,
  setIsAuditorMode,
}: ChecklistProps) => {
  const [userResponses, setUserResponses] = useState<
    Record<string, ChecklistResponseValue>
  >({});
  const [naReasons, setNaReasons] = useState<Record<string, string>>({});
  const [naComments, setNaComments] = useState<Record<string, string>>({});
  const [dirtyNaDetails, setDirtyNaDetails] = useState<Record<string, boolean>>(
    {}
  );
  const [auditorResponses, setAuditorResponses] = useState<
    Record<number, string>
  >({});
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [selectedResources, setSelectedResources] = useState<
    { name: string; url: string }[]
  >([]);
  const [comments, setComments] = useState("");
  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");

  // Initialize collaborative form features
  const collaborativeState = useCollaborativeForm(
    { userResponses, naReasons, naComments, auditorResponses, comments },
    {
      formType: "checklist",
      formId: checklistId || `${certificationId}:${instanceId}`,
      enablePolling: false, // Disable automatic polling to prevent continuous API calls
      pollingInterval: 10000, // 10 seconds when enabled
      showUpdateNotifications: true,
      mergeStrategy: "custom",
      customMerge: (current, update) => {
        // Custom merge logic for checklist data
        const merged = { ...current };

        if (update.userResponses) {
          merged.userResponses = {
            ...current.userResponses,
            ...update.userResponses,
          };
          setUserResponses(merged.userResponses);
        }

        if (update.naReasons) {
          merged.naReasons = { ...current.naReasons, ...update.naReasons };
          setNaReasons(merged.naReasons);
        }

        if (update.naComments) {
          merged.naComments = { ...current.naComments, ...update.naComments };
          setNaComments(merged.naComments);
        }

        if (update.auditorResponses) {
          merged.auditorResponses = {
            ...current.auditorResponses,
            ...update.auditorResponses,
          };
          setAuditorResponses(merged.auditorResponses);
        }

        if (update.comments !== undefined) {
          merged.comments = update.comments;
          setComments(update.comments);
        }

        return merged;
      },
    },
  );

  // Group questions by category
  const categoryGroups: { [category: string]: ChecklistQuestion[] } = {};
  questions.forEach((q) => {
    const category =
      TemporaryQuestionIdToCategoryMapping[Number(q.id)] || "Misc";
    if (!categoryGroups[category]) categoryGroups[category] = [];
    categoryGroups[category].push(q);
  });

  // Initialize user responses from existing answers
  useEffect(() => {
    if (answers.length > 0 && Object.keys(userResponses).length === 0) {
      const initialResponses: Record<string, ChecklistResponseValue> = {};
      const initialReasons: Record<string, string> = {};
      const initialComments: Record<string, string> = {};
      answers.forEach((answer) => {
        const mappedAnswer = mapAnswerToResponse(answer.answer);
        if (mappedAnswer) {
          initialResponses[answer.question_id.toString()] = mappedAnswer;
        }
        if (typeof answer.na_reason === "number") {
          initialReasons[answer.question_id.toString()] =
            answer.na_reason.toString();
        }
        if (answer.na_comment) {
          initialComments[answer.question_id.toString()] = answer.na_comment;
        }
      });
      setUserResponses(initialResponses);
      setNaReasons(initialReasons);
      setNaComments(initialComments);
      setDirtyNaDetails({});
    }
  }, [answers, userResponses]);

  const orderedCategories = getOrderedCategories(labType, tier);
  const categoriesToRender = orderedCategories.filter(
    (cat) => categoryGroups[cat] && categoryGroups[cat].length > 0
  );

  // Check if all questions are answered
  const allQuestionsAnswered = questions.every(
    (q) => userResponses[q.id] !== undefined
  );
  const allNaResponsesHaveReasons = questions.every(
    (q) => userResponses[q.id] !== "na" || Boolean(naReasons[q.id])
  );
  const allNaDetailsSaved = questions.every(
    (q) => userResponses[q.id] !== "na" || !dirtyNaDetails[q.id]
  );

  const isSubmitDisabled =
    !allQuestionsAnswered ||
    !allNaResponsesHaveReasons ||
    !allNaDetailsSaved ||
    isSubmitted;

  // Calculate auditor score
  const yesCount = Object.values(auditorResponses).filter(
    (r) => r === "yes"
  ).length;
  const noCount = Object.values(auditorResponses).filter(
    (r) => r === "no"
  ).length;
  const naCount = Object.values(auditorResponses).filter(
    (r) => r === "na"
  ).length;
  const eligibleCount = yesCount + noCount;
  const auditorScore =
    eligibleCount > 0 ? Math.round((yesCount / eligibleCount) * 100) : 0;

  const handleUserResponseChange = async (
    questionId: string,
    response: ChecklistResponseValue
  ) => {
    if (isSubmitted) return; // Prevent changes if submitted
    setSaveStatus("saving");

    // Optimistic update
    const previousResponses = { ...userResponses };
    const previousReasons = { ...naReasons };
    const previousComments = { ...naComments };

    setUserResponses((prev) => {
      const currentResponse = prev[questionId];
      if (currentResponse === response) {
        const newResponses = { ...prev };
        delete newResponses[questionId];
        // Also clear N/A reason if unchecking N/A
        if (response === "na") {
          setNaReasons((prevReasons) => {
            const newReasons = { ...prevReasons };
            delete newReasons[questionId];
            return newReasons;
          });
          setNaComments((prevComments) => {
            const newComments = { ...prevComments };
            delete newComments[questionId];
            return newComments;
          });
          setDirtyNaDetails((prevDirty) => {
            const newDirty = { ...prevDirty };
            delete newDirty[questionId];
            return newDirty;
          });
        }
        return newResponses;
      } else {
        // Clear N/A reason if switching away from N/A
        if (prev[questionId] === "na" && response !== "na") {
          setNaReasons((prevReasons) => {
            const newReasons = { ...prevReasons };
            delete newReasons[questionId];
            return newReasons;
          });
          setNaComments((prevComments) => {
            const newComments = { ...prevComments };
            delete newComments[questionId];
            return newComments;
          });
          setDirtyNaDetails((prevDirty) => {
            const newDirty = { ...prevDirty };
            delete newDirty[questionId];
            return newDirty;
          });
        }
        if (response === "na") {
          setDirtyNaDetails((prevDirty) => ({
            ...prevDirty,
            [questionId]: true,
          }));
        }
        return {
          ...prev,
          [questionId]: response,
        };
      }
    });

    // API Call
    try {
      // Determine the new answer value
      let apiAnswer: "y" | "n" | "x" = "y";
      if (response === "no") apiAnswer = "n";
      if (response === "na") apiAnswer = "x";

      // If we are toggling off (current response matches new response), we might want to delete the answer?
      // The current UI logic toggles off if clicked again.
      // However, the API `saveAnswer` expects an answer.
      // If the user unchecks a box, effectively removing the answer, we might need to handle that.
      // Looking at `ChecklistService.saveAnswer`, it takes `Answer` type 'y' | 'n' | 'x'.
      // It doesn't seem to support "null" or "delete".
      // For now, I will assume we only save when setting a value.
      // If the user unchecks, we might need a way to clear it, but `saveAnswer` signature doesn't show a clear way to "unset".
      // Let's assume for now we only save when a value is set.
      // Wait, if I uncheck, `userResponses[questionId]` becomes undefined.
      // If I click the SAME checkbox again, it toggles off.

      const isUnchecking = userResponses[questionId] === response;

      if (!isUnchecking) {
        if (response === "na") {
          setSaveStatus("idle");
          return;
        }

        await ChecklistService.saveChecklistInstanceAnswer(
          certificationId,
          instanceId,
          Number(questionId),
          apiAnswer,
        );
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus("idle"), 2000);
      } else {
        // We are unchecking.
        setSaveStatus("idle");
      }
    } catch (error) {
      console.error("Failed to save answer:", error);
      toast.error("Failed to save answer");
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 2000);
      // Revert state
      setUserResponses(previousResponses);
      setNaReasons(previousReasons);
      setNaComments(previousComments);
    }
  };

  const handleNaReasonChange = (questionId: string, reason: string) => {
    if (isSubmitted) return;

    setNaReasons((prev) => ({
      ...prev,
      [questionId]: reason,
    }));
    setDirtyNaDetails((prev) => ({ ...prev, [questionId]: true }));
  };

  const handleNaCommentChange = (questionId: string, comment: string) => {
    if (isSubmitted) return;

    setNaComments((prev) => ({
      ...prev,
      [questionId]: comment,
    }));
    setDirtyNaDetails((prev) => ({ ...prev, [questionId]: true }));
  };

  const handleNaDetailsSave = async (questionId: string) => {
    if (isSubmitted || userResponses[questionId] !== "na") return;

    const reason = naReasons[questionId];
    if (!reason) {
      toast.error("Select an N/A reason before saving");
      return;
    }

    setSaveStatus("saving");
    try {
      await ChecklistService.saveChecklistInstanceAnswer(
        certificationId,
        instanceId,
        Number(questionId),
        "x",
        parseInt(reason),
        naComments[questionId]?.trim() || undefined
      );
      setDirtyNaDetails((prev) => {
        const next = { ...prev };
        delete next[questionId];
        return next;
      });
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2000);
    } catch (error) {
      console.error("Failed to save N/A details:", error);
      toast.error("Failed to save N/A details");
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 2000);
    }
  };

  const handleResourceClick = (category: string) => {
    const resources = getResourcesForCategory(tier, category);
    if (resources && resources.length > 0) {
      setSelectedResources(resources);
      setShowResourceModal(true);
    }
  };

  const handleSubmitClick = () => {
    setShowSubmitModal(true);
  };

  const handleConfirmSubmit = async () => {
    setShowSubmitModal(false);
    answerSubmit(comments);
    // The redirect will be handled by the ChecklistPage component after API success
  };

  return (
    <div>
      {/* Collaboration Status Indicator — only render when there is something to
          show. The connection-status dot is commented out in CollaborationIndicator,
          so with no remote updates the bar would otherwise render empty. */}
      {!isSubmitted && !previewMode &&
        (collaborativeState.hasRemoteUpdates || collaborativeState.updateCount > 0) && (
        <div className="mb-4 flex justify-between items-center bg-base-100 p-2 rounded-lg shadow-sm border border-base-200">
          <div className="flex items-center gap-4">
            <CollaborationIndicator
              isConnected={collaborativeState.isConnected}
              hasUpdates={collaborativeState.hasRemoteUpdates}
              updateCount={collaborativeState.updateCount}
              lastUpdateBy={collaborativeState.lastUpdate?.updatedBy?.userName}
              onRefresh={collaborativeState.fetchLatestUpdates}
              className=""
            />
          </div>

          {collaborativeState.hasRemoteUpdates && (
            <button
              onClick={collaborativeState.acknowledgeUpdates}
              className="btn btn-xs btn-ghost"
            >
              Dismiss Updates
            </button>
          )}
        </div>
      )}

      {/* Submission Banner */}
      {isSubmitted && (
        <Alert variant="success" className="mb-6">
          <div className="flex items-center justify-between">
            <span>
              This checklist has been submitted and is now locked for editing.
            </span>
            <button
              className="btn btn-sm btn-outline"
              onClick={() => (window.location.href = "/lab-dashboard")}
            >
              Return to Dashboard
            </button>
          </div>
        </Alert>
      )}

      {/* Mode Toggle */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium">View Mode:</span>
          <button
            className={`btn btn-sm ${!isAuditorMode ? "btn-primary" : "btn-outline"}`}
            onClick={() => setIsAuditorMode(false)}
            disabled={isSubmitted}
          >
            Actions Checklist
          </button>
          <button
            className={`btn btn-sm ${isAuditorMode ? "btn-primary" : "btn-outline"}`}
            onClick={() => setIsAuditorMode(true)}
            disabled={isSubmitted}
          >
            Auditor Checklist
          </button>
        </div>
        <div className="text-sm text-gray-600">
          {isAuditorMode
            ? "Auditor Checklist Mode: Review and provide feedback"
            : "Actions Checklist Mode: Complete your sustainability checklist"}
          {!isAuditorMode && !isSubmitted && (
            <div className="text-xs mt-1">
              {allQuestionsAnswered ? (
                <span className="text-green-600">
                  All questions answered - ready to submit
                </span>
              ) : (
                <span className="text-orange-600">
                  Please answer all questions before submitting
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-8 mb-10">
        {/* User Checklist */}
        {!isAuditorMode && (
        <UpdateAnimationOverlay show={collaborativeState.updateAnimation}>
          <div className="overflow-x-auto rounded-lg border border-gray-300">
            <table className="table table-fixed w-full border-collapse rounded-lg shadow-sm ">
              <colgroup>
                <col style={{ width: "3rem" }} />
                <col />
                <col style={{ width: "5rem" }} />
                <col style={{ width: "5rem" }} />
                <col style={{ width: "5rem" }} />
              </colgroup>
              <tbody>
                {(() => {
                  let globalQuestionCounter = 1;
                  return categoriesToRender.map((category) => {
                    const categoryQuestions = categoryGroups[category] || [];

                    return (
                      <React.Fragment key={category}>
                        {/* Category Header Row */}
                        <tr className="">
                          <td className="bg-[#63d994] border border-gray-300 border-l-0 border-t-0"></td>
                          <td className="bg-[#63d994] text-[#4d4a4a] font-bold p-4 border border-gray-300 border-t-0">
                            <div className="flex justify-between items-center">
                              <span className="text-lg font-bold">
                                {category}
                              </span>
                              <div className="flex items-center gap-4">
                                {getResourcesForCategory(tier, category) &&
                                  !isSubmitted && (
                                    <button
                                      className={`btn ${tierButtonColors[tier].bg} ${tierButtonColors[tier].hover} text-[#4d4a4a] border-none`}
                                      onClick={() =>
                                        handleResourceClick(category)
                                      }
                                    >
                                      <img
                                        width={15}
                                        src={icons.download}
                                        alt="download"
                                      />
                                      Download Resource
                                    </button>
                                  )}
                              </div>
                            </div>
                          </td>
                          <td className="bg-[#63d994]  p-2 text-center font-bold text-[#4d4a4a] border border-gray-300 border-t-0">
                            Yes
                          </td>
                          <td className="bg-[#63d994]  p-2 text-center font-bold text-[#4d4a4a] border border-gray-300 border-t-0">
                            No
                          </td>
                          <td className="bg-[#63d994]  p-2 text-center font-bold text-[#4d4a4a] border border-gray-300 border-r-0 border-t-0">
                            N/A
                          </td>
                        </tr>

                        {/* Question rows */}
                        {categoryQuestions.map((q) => {
                            const currentQuestionNumber =
                              globalQuestionCounter++;
                            const currentResponse = userResponses[q.id];

                            return (
                              <tr key={q.id} className="">
                                <td className="border border-gray-300 border-b-0 p-3 text-center font-medium w-12 bg-[#63d994] border-l-0">
                                  {currentQuestionNumber}
                                </td>
                                <td className="border border-gray-300 border-b-0 p-3">
                                  {q.question}
                                  {currentResponse === "na" && (
                                    <div className="mt-2 flex max-w-xl flex-col gap-2">
                                      <select
                                        className="select select-sm w-full max-w-xs outline-none focus:outline-none focus-visible:outline-none active:outline-none hover:outline-none rounded-lg"
                                        value={naReasons[q.id] || ""}
                                        onChange={(e) =>
                                          handleNaReasonChange(
                                            q.id,
                                            e.target.value,
                                          )
                                        }
                                        disabled={isSubmitted || previewMode}
                                      >
                                        <option value="">
                                          Select reason...
                                        </option>
                                        <option value="1">
                                          Internal Policy
                                        </option>
                                        <option value="2">OHS Rules</option>
                                        <option value="3">
                                          Building/Equipment Limits
                                        </option>
                                        <option value="4">
                                          External Control
                                        </option>
                                        <option value="5">Other</option>
                                      </select>
                                      <textarea
                                        className="textarea textarea-bordered textarea-sm w-full rounded-lg"
                                        value={naComments[q.id] || ""}
                                        onChange={(e) =>
                                          handleNaCommentChange(
                                            q.id,
                                            e.target.value,
                                          )
                                        }
                                        placeholder="Add a short note about why this item is not applicable."
                                        disabled={isSubmitted || previewMode}
                                      />
                                      <button
                                        className="btn btn-primary btn-sm w-fit"
                                        onClick={() => handleNaDetailsSave(q.id)}
                                        disabled={isSubmitted || previewMode || !naReasons[q.id]}
                                      >
                                        Save N/A details
                                      </button>
                                      {dirtyNaDetails[q.id] && (
                                        <p className="text-xs text-base-content/60">
                                          Save these N/A details before submitting the checklist.
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </td>
                                <td className="border border-gray-300 border-b-0 p-3 w-20 text-center">
                                  <input
                                    type="checkbox"
                                    className="checkbox checkbox-primary"
                                    checked={currentResponse === "yes"}
                                    onChange={() =>
                                      handleUserResponseChange(q.id, "yes")
                                    }
                                    disabled={isSubmitted || previewMode}
                                  />
                                </td>
                                <td className="border border-gray-300 border-b-0 p-3 w-20 text-center">
                                  <input
                                    type="checkbox"
                                    className="checkbox checkbox-primary"
                                    checked={currentResponse === "no"}
                                    onChange={() =>
                                      handleUserResponseChange(q.id, "no")
                                    }
                                    disabled={isSubmitted || previewMode}
                                  />
                                </td>
                                <td className="border border-gray-300 border-r-0 border-b-0 p-3 w-20 text-center">
                                  <input
                                    type="checkbox"
                                    className="checkbox checkbox-primary"
                                    checked={currentResponse === "na"}
                                    onChange={() =>
                                      handleUserResponseChange(q.id, "na")
                                    }
                                    disabled={isSubmitted || previewMode}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                      </React.Fragment>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        </UpdateAnimationOverlay>
      )}

        {/* Auditor Checklist */}
        {isAuditorMode && (
          <AuditorChecklistSection
            labType={labType}
            isSubmitted={isSubmitted}
            responses={auditorResponses}
            onResponseChange={setAuditorResponses}
            auditorScore={auditorScore}
            yesCount={yesCount}
            noCount={noCount}
            naCount={naCount}
            eligibleCount={eligibleCount}
            isActualAuditor={auditorMode}
          />
        )}
      </div>

      {!isSubmitted && !previewMode && (
        <SaveStatusIndicator status={saveStatus} className="mb-4" />
      )}

      {/* Comments and Action Buttons */}
      <div className="flex flex-row gap-4 justify-between items-center">
        <div className="grow flex flex-col">
          <label
            htmlFor="comments"
            className="label mb-4 text-[#4d4a4a] font-bold"
          >
            Comments
          </label>
          <textarea
            name="comments"
            id="comments"
            className="textarea w-full whitespace-normal rounded-lg"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            disabled={isSubmitted || previewMode}
          />
        </div>

        <div className="flex flex-row-reverse gap-4">
          {!isAuditorMode ? (
            // User Mode Buttons
            <button
              className="btn btn-primary"
              onClick={handleSubmitClick}
              disabled={isSubmitDisabled || previewMode}
            >
              Submit Checklist
            </button>
          ) : (
            // Auditor Mode Buttons - Only visible to actual auditors
            auditorMode && (
              <AuditorActionButtons
                isSubmitted={isSubmitted}
                score={auditorScore}
                responses={auditorResponses}
                questions={
                  labType === "wet"
                    ? wetLabAuditorQuestions
                    : dryLabAuditorQuestions
                }
              />
            )
          )}
        </div>
      </div>

      {/* Modals */}
      <ConfirmationModal
        isOpen={showSubmitModal}
        onClose={() => setShowSubmitModal(false)}
        onConfirm={handleConfirmSubmit}
        title="Confirm Submission"
        message="Once you submit, you will not be able to return and edit this checklist. Are you sure you want to proceed?"
        confirmText="Submit & Lock"
        cancelText="Go Back"
      />

      <ResourceModal
        isOpen={showResourceModal}
        onClose={() => setShowResourceModal(false)}
        resources={selectedResources}
      />
    </div>
  );
};

// Updated AuditorChecklistSection with scoring
const AuditorChecklistSection = ({
  labType,
  isSubmitted = false,
  responses,
  onResponseChange,
  auditorScore,
  yesCount,
  noCount,
  naCount,
  eligibleCount,
  isActualAuditor = false,
}: {
  labType: "wet" | "dry" | undefined;
  isSubmitted?: boolean;
  responses: Record<number, string>;
  onResponseChange: (responses: Record<number, string>) => void;
  auditorScore: number;
  yesCount: number;
  noCount: number;
  naCount: number;
  eligibleCount: number;
  isActualAuditor?: boolean;
}) => {
  const questions =
    labType === "wet" ? wetLabAuditorQuestions : dryLabAuditorQuestions;

  const auditorCategoryGroups: { [category: string]: AuditorQuestion[] } = {};
  questions.forEach((q) => {
    const category = TemporaryQuestionIdToCategoryMapping[q.id] || "Misc";
    if (!auditorCategoryGroups[category]) auditorCategoryGroups[category] = [];
    auditorCategoryGroups[category].push(q);
  });

  // Note: Auditor checklist currently defaults to bronze tier categories
  const orderedCategories = getOrderedCategories(labType || "wet", "bronze");
  const categoriesToRender = orderedCategories.filter(
    (cat: string) =>
      auditorCategoryGroups[cat] && auditorCategoryGroups[cat].length > 0,
  );

  const handleResponseChange = (questionId: number, response: string) => {
    if (isSubmitted) return;

    const currentResponse = responses[questionId];
    if (currentResponse === response) {
      const newResponses = { ...responses };
      delete newResponses[questionId];
      onResponseChange(newResponses);
    } else {
      onResponseChange({
        ...responses,
        [questionId]: response,
      });
    }
  };

  return (
    <div>
      {/* Read-only notice for lab users */}
      {!isActualAuditor && (
        <div className="alert bg-amber-50 mb-4 border-2 border-solid border-[#eaddc9] rounded-lg shadow-md">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="stroke-current shrink-0 h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <div className="font-semibold">View-Only Mode</div>
            <div className="text-sm">
              You are viewing the auditor checklist in read-only mode. Only
              auditors can interact with these items.
            </div>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-300">
        <table className="table w-full border-collapse rounded-lg shadow-sm ">
          <tbody>
            {(() => {
              let globalQuestionCounter = 1;
              return categoriesToRender.map((category: string) => {
                const categoryQuestions = auditorCategoryGroups[category] || [];

                return (
                  <React.Fragment key={category}>
                    {/* Category Header Row */}
                    <tr>
                      <td className="bg-[#63d994] border border-gray-300 border-l-0 border-t-0"></td>
                      <td
                        className={`bg-[#63d994] p-3 font-bold text-[#4d4a4a] text-lg border border-gray-300 border-t-0 ${!isActualAuditor ? "border-r-0" : ""}`}
                      >
                        {category}
                      </td>
                      {isActualAuditor && (
                        <>
                          <td className="bg-[#63d994]  p-2 text-center font-bold text-[#4d4a4a] border border-gray-300 border-t-0">
                            Yes
                          </td>
                          <td className="bg-[#63d994]  p-2 text-center font-bold text-[#4d4a4a] border border-gray-300 border-t-0">
                            No
                          </td>
                          <td className="bg-[#63d994]  p-2 text-center font-bold text-[#4d4a4a] border border-gray-300 border-t-0">
                            N/A
                          </td>
                          <td className="bg-[#63d994]  p-3 text-center font-bold text-[#4d4a4a] border border-gray-300 border-t-0 border-r-0">
                            If "No" - Suggested Actions
                          </td>
                        </>
                      )}
                    </tr>

                    {/* Question Rows */}
                    {categoryQuestions.map((question) => {
                      const currentQuestionNumber = globalQuestionCounter++;
                      const currentResponse = responses[question.id];

                      return (
                        <tr key={question.id} className="">
                          <td className="border border-gray-300 border-l-0 bg-[#63d994]">
                            <span className="font-bold text-[#4d4a4a] min-w-[20px]">
                              {currentQuestionNumber}
                            </span>
                          </td>
                          <td
                            className={`border border-gray-300 border-b-0 p-3 ${!isActualAuditor ? "border-r-0" : ""}`}
                          >
                            <div className="flex gap-2">
                              <span className="text-[#4d4a4a]">
                                {question.question}
                              </span>
                            </div>
                          </td>
                          {isActualAuditor && (
                            <>
                              <td className="border border-gray-300 border-b-0 p-3 text-center w-16">
                                <input
                                  type="checkbox"
                                  className="checkbox checkbox-primary"
                                  checked={currentResponse === "yes"}
                                  onChange={() =>
                                    handleResponseChange(question.id, "yes")
                                  }
                                  disabled={isSubmitted}
                                />
                              </td>
                              <td className="border border-gray-300 border-b-0 p-3 text-center w-16">
                                <input
                                  type="checkbox"
                                  className="checkbox checkbox-primary"
                                  checked={currentResponse === "no"}
                                  onChange={() =>
                                    handleResponseChange(question.id, "no")
                                  }
                                  disabled={isSubmitted}
                                />
                              </td>
                              <td className="border border-gray-300 border-b-0 p-3 text-center w-16">
                                <input
                                  type="checkbox"
                                  className="checkbox checkbox-primary"
                                  checked={currentResponse === "na"}
                                  onChange={() =>
                                    handleResponseChange(question.id, "na")
                                  }
                                  disabled={isSubmitted}
                                />
                              </td>
                              <td className="border border-gray-300 border-b-0 border-r-0 p-3 text-[#4d4a4a]">
                                {question.suggestion}
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              });
            })()}
          </tbody>
        </table>
      </div>

      {/* Score Panel - Only visible to auditors */}
      {isActualAuditor && (
        <div className="mt-8 p-6 bg-gray-50 rounded-xl border shadow-sm">
          <h3 className="text-lg font-bold mb-4">Audit Score</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {yesCount}
              </div>
              <div className="text-sm text-gray-600">Yes</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{noCount}</div>
              <div className="text-sm text-gray-600">No</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{naCount}</div>
              <div className="text-sm text-gray-600">N/A</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {eligibleCount}
              </div>
              <div className="text-sm text-gray-600">Eligible</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {auditorScore}%
              </div>
              <div className="text-sm text-gray-600">Score</div>
            </div>
          </div>
          <div className="text-sm text-gray-600">
            Score calculation: Yes ({yesCount}) ÷ Eligible ({eligibleCount}) ×
            100 = {auditorScore}%
            {naCount > 0 && ` (${naCount} N/A responses excluded)`}
          </div>
        </div>
      )}
    </div>
  );
};

// Auditor Action Buttons Component
interface AuditorActionButtonsProps {
  isSubmitted?: boolean;
  score: number;
  responses: Record<number, string>;
  questions: AuditorQuestion[];
}

const AuditorActionButtons = ({
  isSubmitted = false,
  score,
  responses,
  questions,
}: AuditorActionButtonsProps) => {
  const [showAwardModal, setShowAwardModal] = useState(false);
  const [showResubmitModal, setShowResubmitModal] = useState(false);

  const handleAwardCertification = () => {
    console.log("Awarding certification...");
    // TODO: API call to update audit status to "Certification Awarded"
    // TODO: Send notifications to LAB ZER0 Champion & admins
    setShowAwardModal(false);
  };

  const handleRequestResubmission = () => {
    console.log("Requesting resubmission...");
    // TODO: API call to create new Actions checklist draft
    // TODO: Send improvement pack email to LAB ZER0 Champion
    setShowResubmitModal(false);
  };

  // Get "No" responses grouped by category for resubmission preview
  const getNoResponsesByCategory = () => {
    const noResponses: { [category: string]: AuditorQuestion[] } = {};

    questions.forEach((question) => {
      if (responses[question.id] === "no") {
        const category =
          TemporaryQuestionIdToCategoryMapping[question.id] || "Misc";
        if (!noResponses[category]) noResponses[category] = [];
        noResponses[category].push(question);
      }
    });

    return noResponses;
  };

  return (
    <>
      {score >= 80 ? (
        <button
          className="btn btn-primary"
          onClick={() => setShowAwardModal(true)}
          disabled={isSubmitted}
        >
          Award Certification
        </button>
      ) : (
        <button
          className="btn btn-warning"
          onClick={() => setShowResubmitModal(true)}
          disabled={isSubmitted}
        >
          Request Resubmission
        </button>
      )}

      {/* Award Certification Modal */}
      <ConfirmationModal
        isOpen={showAwardModal}
        onClose={() => setShowAwardModal(false)}
        onConfirm={handleAwardCertification}
        title="Award Certification"
        message="Award certification for this lab. This will lock the audit."
        confirmText="Award Certification"
      />

      {/* Request Resubmission Modal with No Responses Preview */}
      <div className={`modal ${showResubmitModal ? "modal-open" : ""}`}>
        <div className="modal-box max-w-4xl rounded-xl">
          <h3 className="font-bold text-lg mb-4">Request Resubmission</h3>
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-4">
              The following items need improvement. An Improvement Pack with
              these suggestions will be sent to the LAB ZER0 Champion.
            </p>

            {Object.entries(getNoResponsesByCategory()).map(
              ([category, questions]) => (
                <div key={category} className="mb-6">
                  <h4 className="font-semibold text-md mb-2 text-orange-700">
                    {category}
                  </h4>
                  <div className="space-y-3">
                    {questions.map((question) => (
                      <div
                        key={question.id}
                        className="border-l-4 border-orange-300 pl-4 py-2 bg-orange-50"
                      >
                        <p className="text-sm font-medium text-gray-800 mb-1">
                          {question.question}
                        </p>
                        <p className="text-xs text-gray-600 italic">
                          <strong>Suggestion:</strong> {question.suggestion}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ),
            )}

            {Object.keys(getNoResponsesByCategory()).length === 0 && (
              <p className="text-gray-500 italic">
                No improvement items found.
              </p>
            )}
          </div>

          <div className="modal-action">
            <button
              className="btn btn-ghost"
              onClick={() => setShowResubmitModal(false)}
            >
              Cancel
            </button>
            <button
              className="btn btn-warning"
              onClick={handleRequestResubmission}
            >
              Send Improvement Pack
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
